import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { register as registerService } from '../services/authService';

const Register = () => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const validatePassword = (pwd: string) => {
        if (pwd.length < 6) return 'Password must be at least 6 characters!';
        if (pwd.length > 10) return 'Password must not exceed 10 characters!';
        if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one capital letter!';
        if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number!';
        if (!/[^A-Za-z0-9]/.test(pwd)) return 'Password must contain at least one special character!';
        return '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match!');
            return;
        }

        const validationError = validatePassword(password);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        try {
            const response = await registerService(fullName, email, password);
            login(response);
            const redirect = new URLSearchParams(window.location.search).get('redirect');
            navigate(redirect || '/dashboard');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Registration failed!');
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrength = () => {
        if (!password) return null;
        const checks = [
            password.length >= 6 && password.length <= 10,
            /[A-Z]/.test(password),
            /[0-9]/.test(password),
            /[^A-Za-z0-9]/.test(password),
        ];
        const passed = checks.filter(Boolean).length;
        if (passed <= 1) return { label: 'Weak', color: 'bg-red-500', width: 'w-1/4' };
        if (passed === 2) return { label: 'Fair', color: 'bg-yellow-500', width: 'w-2/4' };
        if (passed === 3) return { label: 'Good', color: 'bg-blue-500', width: 'w-3/4' };
        return { label: 'Strong', color: 'bg-green-500', width: 'w-full' };
    };

    const strength = getPasswordStrength();

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 md:px-6">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/">
                        <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                            TubeScript AI
                        </span>
                    </Link>
                    <p className="text-gray-400 mt-2">Create your free account today!</p>
                </div>

                {/* Form */}
                <div className="bg-gray-800 rounded-2xl p-6 md:p-8 border border-gray-700">
                    {error && (
                        <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-gray-300 text-sm font-medium mb-2">
                                Full Name
                            </label>
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="Ijeoma Muoghalu"
                                required
                                className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-300 text-sm font-medium mb-2">
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-300 text-sm font-medium mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    maxLength={10}
                                    className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 pr-12 rounded-xl focus:outline-none focus:border-purple-500 transition"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition">
                                    {showPassword ? '🙈' : '👁️'}
                                </button>
                            </div>

                            {/* Password strength bar */}
                            {password && strength && (
                                <div className="mt-2">
                                    <div className="w-full bg-gray-700 rounded-full h-1.5">
                                        <div className={`${strength.color} ${strength.width} h-1.5 rounded-full transition-all`} />
                                    </div>
                                    <p className={`text-xs mt-1 ${
                                        strength.label === 'Weak' ? 'text-red-400' :
                                        strength.label === 'Fair' ? 'text-yellow-400' :
                                        strength.label === 'Good' ? 'text-blue-400' :
                                        'text-green-400'
                                    }`}>{strength.label} password</p>
                                </div>
                            )}

                            {/* Password requirements */}
                            {password && (
                                <ul className="mt-2 space-y-1">
                                    {[
                                        { check: password.length >= 6 && password.length <= 10, label: 'Between 6-10 characters' },
                                        { check: /[A-Z]/.test(password), label: 'One capital letter' },
                                        { check: /[0-9]/.test(password), label: 'One number' },
                                        { check: /[^A-Za-z0-9]/.test(password), label: 'One special character' },
                                    ].map((req) => (
                                        <li key={req.label} className={`text-xs flex items-center gap-1 ${req.check ? 'text-green-400' : 'text-gray-500'}`}>
                                            {req.check ? '✅' : '⭕'} {req.label}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-300 text-sm font-medium mb-2">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    maxLength={10}
                                    className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 pr-12 rounded-xl focus:outline-none focus:border-purple-500 transition"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition">
                                    {showConfirmPassword ? '🙈' : '👁️'}
                                </button>
                            </div>
                            {confirmPassword && (
                                <p className={`text-xs mt-1 ${password === confirmPassword ? 'text-green-400' : 'text-red-400'}`}>
                                    {password === confirmPassword ? '✅ Passwords match' : '❌ Passwords do not match'}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition">
                            {loading ? 'Creating account...' : 'Create Free Account'}
                        </button>
                    </form>

                    <p className="text-center text-gray-400 mt-6 text-sm">
                        Already have an account?{' '}
                        <Link to="/login" className="text-purple-400 hover:text-purple-300 transition">
                            Login here
                        </Link>
                    </p>
                </div>

                <p className="text-center text-gray-500 text-xs mt-4">
                    ✅ Free plan includes 5 scripts/month — No credit card required!
                </p>
            </div>
        </div>
    );
};

export default Register;


